package kz.dentalcrm.app.screens.employees.add

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_employee_added
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.ui.ScreenState
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.request_model.AddEmployeeBody
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.screens.employees.add.mvi.AddEmployeeSideEffect
import kz.dentalcrm.app.screens.employees.add.mvi.AddEmployeeState
import kz.dentalcrm.app.screens.employees.add.mvi.AddEmployeeViewEvent
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class AddEmployeeViewModel : BaseStateViewModel<
        AddEmployeeViewEvent,
        AddEmployeeState,
        AddEmployeeSideEffect>() {

    private val appointmentRepository: AppointmentRepository by inject()

    override fun setInitialState(): AddEmployeeState = AddEmployeeState()

    override fun handleEvents(event: AddEmployeeViewEvent) {
        when (event) {
            is AddEmployeeViewEvent.LoadInitialData -> {
                loadInitialData()
            }

            is AddEmployeeViewEvent.UpdateName -> {
                setState {
                    copy(
                        name = event.value,
                        nameError = if (event.value.isEmpty()) "Имя обязательно" else ""
                    )
                }
            }

            is AddEmployeeViewEvent.UpdateSurname -> {
                setState {
                    copy(
                        surname = event.value,
                        surnameError = if (event.value.isEmpty()) "Фамилия обязательна" else ""
                    )
                }
            }

            is AddEmployeeViewEvent.UpdatePatronymic -> {
                setState {
                    copy(patronymic = event.value)
                }
            }

            is AddEmployeeViewEvent.UpdatePhone -> {
                setState {
                    copy(
                        phone = event.value,
                        phoneError = if (event.value.isEmpty()) "Телефон обязателен" else ""
                    )
                }
            }

            is AddEmployeeViewEvent.UpdateGender -> {
                setState {
                    copy(gender = event.value)
                }
            }

            is AddEmployeeViewEvent.UpdateBirthDate -> {
                setState {
                    copy(birthDate = event.value)
                }
            }

            is AddEmployeeViewEvent.UpdateColor -> {
                setState {
                    copy(color = event.value)
                }
            }

            is AddEmployeeViewEvent.UpdatePositions -> {
                setState {
                    copy(selectedPositions = event.positions)
                }
            }

            is AddEmployeeViewEvent.UpdateServices -> {
                setState {
                    copy(selectedServices = event.services)
                }
            }

            is AddEmployeeViewEvent.UpdateCreateAccount -> {
                setState {
                    copy(createAccount = event.value)
                }
            }

            is AddEmployeeViewEvent.UpdateEmail -> {
                setState {
                    copy(
                        email = event.value,
                        emailError = if (event.value.isEmpty()) "Email обязателен" else ""
                    )
                }
            }

            is AddEmployeeViewEvent.UpdatePassword -> {
                setState {
                    copy(
                        password = event.value,
                        passwordError = if (event.value.isEmpty()) "Пароль обязателен" else ""
                    )
                }
            }

            is AddEmployeeViewEvent.TogglePermission -> {
                setState {
                    val newPermissions = if (selectedPermissions.contains(event.permission)) {
                        selectedPermissions - event.permission
                    } else {
                        selectedPermissions + event.permission
                    }
                    copy(selectedPermissions = newPermissions)
                }
            }

            is AddEmployeeViewEvent.SaveEmployee -> {
                saveEmployee()
            }

            is AddEmployeeViewEvent.ResetState -> {
                setState { AddEmployeeState() }
            }
        }
    }

    private fun loadInitialData() {
        setState { copy(screenState = ScreenState.Loading) }

        launch {
            var positionsLoaded = false
            var servicesLoaded = false
            var permissionsLoaded = false
            var hasError = false

            // Load positions
            appointmentRepository.getPositions().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { AddEmployeeSideEffect.ShowError(msg ?: "Failed to load positions") }
                },
                onSuccess = { positions ->
                    positionsLoaded = true
                    setState {
                        copy(
                            availablePositions = positions,
                            screenState = if ((servicesLoaded && permissionsLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                        )
                    }
                }
            )

            // Load services
            appointmentRepository.getServicesArray().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { AddEmployeeSideEffect.ShowError(msg ?: "Failed to load services") }
                },
                onSuccess = { services ->
                    servicesLoaded = true
                    setState {
                        copy(
                            availableServices = services,
                            screenState = if ((positionsLoaded && permissionsLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                        )
                    }
                }
            )

            // Load permissions
            appointmentRepository.getPermissions().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect {
                        AddEmployeeSideEffect.ShowError(
                            msg ?: "Failed to load permissions"
                        )
                    }
                },
                onSuccess = { permissions ->
                    permissionsLoaded = true
                    setState {
                        copy(
                            availablePermissions = permissions,
                            screenState = ScreenState.Idle
                        )
                    }
                }
            )
        }
    }

    private fun saveEmployee() {
        val state = viewState.value

        // Validation
        if (state.name.isEmpty()) {
            setState { copy(nameError = "Имя обязательно") }
            return
        }
        if (state.surname.isEmpty()) {
            setState { copy(surnameError = "Фамилия обязательна") }
            return
        }
        if (state.phone.isEmpty()) {
            setState { copy(phoneError = "Телефон обязателен") }
            return
        }
        if (state.gender.isEmpty()) {
            setEffect { AddEmployeeSideEffect.ShowError("Пол обязателен") }
            return
        }
        if (state.birthDate.isEmpty()) {
            setEffect { AddEmployeeSideEffect.ShowError("Дата рождения обязательна") }
            return
        }

        // Validate account creation fields if enabled
        if (state.createAccount) {
            if (state.email.isEmpty()) {
                setState { copy(emailError = "Email обязателен") }
                return
            }
            if (state.password.isEmpty()) {
                setState { copy(passwordError = "Пароль обязателен") }
                return
            }
        }

        setState { copy(screenState = ScreenState.Loading) }

        val employeeBody = AddEmployeeBody(
            name = state.name,
            surname = state.surname,
            patronymic = state.patronymic,
            phone = state.phone,
            gender = state.gender,
            birth_date = state.birthDate,
            color = state.color,
            positions = state.selectedPositions.ifEmpty { null },
            services = state.selectedServices.ifEmpty { null },
            email = if (state.createAccount) state.email else null,
            password = if (state.createAccount) state.password else null,
            permissions = if (state.createAccount && state.selectedPermissions.isNotEmpty()) state.selectedPermissions else null,
            create_account = state.createAccount,
            role_id = state.roleId
        )

        launch {
            appointmentRepository.addEmployee(employeeBody).stateHandle(
                onError = { _, msg ->
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { AddEmployeeSideEffect.ShowError(msg ?: "Failed to save employee") }
                },
                onFailure = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { AddEmployeeSideEffect.ShowError("Failed to save employee") }
                },
                onSuccess = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_employee_added), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { AddEmployeeSideEffect.SaveSuccess }
                }
            )
        }
    }
}
